"use client"
import {
  Alert,
  Box,
  Button,
  FormLabel,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material"
import { UploadResult, useXmlSubmit } from "@tc/ui-dfe/hooks/useXmlSubmit"
import {
  DeleteIcon,
  DragDropIcon,
  FileErrorIcon,
  FileIcon,
  FileSuccessIcon,
  IdeasIcon,
  UploadIcon,
} from "@tc/ui-dfe/icons"
import { XmlRequestType } from "@tc/ui-dfe/query"
import { DragEvent, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { FormSelector } from "../Config/Form/FormSelector/FormSelector"
import { Modal } from "../Modal/Modal"

export interface XmlSubmitModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: (responseId?: string, requestType?: XmlRequestType) => void
}

const requestTypeOptions = [
  { label: "AP export", value: XmlRequestType.ApExport },
  { label: "Phyto export", value: XmlRequestType.PhytoExport },
  { label: "Transfer", value: XmlRequestType.Transfer },
] as const

export const XmlSubmitModal = ({
  open,
  onClose,
  onSuccess,
}: XmlSubmitModalProps) => {
  const methods = useForm<{ requestType: XmlRequestType | "" }>({
    defaultValues: {
      requestType: "",
    },
  })
  const requestType = methods.watch("requestType")
  const {
    uploadProgress,
    isUploading,
    isProcessing,
    errors: hookErrors,
    uploadFile,
    submitXml,
    reset,
  } = useXmlSubmit()

  const [file, setFile] = useState<File | null>(null)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [localErrors, setLocalErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const errors = [...localErrors, ...hookErrors]
  const submitting = isUploading || isProcessing

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const resetForm = () => {
    methods.reset()
    setFile(null)
    setUploadResult(null)
    setLocalErrors([])
    reset()
  }

  const validateFile = (file: File): string[] => {
    const errors: string[] = []

    if (!file.name.toLowerCase().endsWith(".xml")) {
      errors.push("File must be in XML format")
    }

    if (file.size > 50 * 1024 * 1024) {
      errors.push("File size must not exceed 50MB")
    }

    return errors
  }

  const processFileSelection = async (selectedFile: File) => {
    const validationErrors = validateFile(selectedFile)

    setFile(selectedFile)
    setLocalErrors(validationErrors)
    setUploadResult(null)

    if (validationErrors.length > 0) return

    const result = await uploadFile(selectedFile)
    setUploadResult(result)
  }

  const handleFileSelect = async (selectedFile: File) => {
    await processFileSelection(selectedFile)
  }

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    await processFileSelection(selectedFile)
  }

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const droppedFile = event.dataTransfer.files[0]
    if (droppedFile) {
      await handleFileSelect(droppedFile)
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleChooseFile = () => {
    fileInputRef.current?.click()
  }

  const handleDeleteFile = () => {
    setFile(null)
    setLocalErrors([])
    setUploadResult(null)
    reset()
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSubmit = async () => {
    if (!uploadResult || !requestType) return

    setLocalErrors([])

    const result = await submitXml(uploadResult, requestType)

    if (result.success) {
      handleClose()
      onSuccess?.(result.responseId, requestType)
    }
  }

  const isSubmitDisabled =
    !requestType || !file || !uploadResult || errors.length > 0 || submitting

  return (
    <Modal
      open={open}
      onClose={handleClose}
      header="Create request with XML upload"
      maxWidth="sm"
      disableFullScreenButton
      actions={
        <>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            Submit
          </Button>
        </>
      }
    >
      <FormProvider {...methods}>
        <Stack spacing={3}>
          {/* Tips Banner */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              bgcolor: "#F4F4F4",
              border: "1px solid #E9E9E9",
              borderRadius: 1,
            }}
          >
            <IdeasIcon sx={{ width: 16, height: 16 }} fill="forest.main" />
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Tips:
              </Box>{" "}
              Accepted format: .xml, Max size: 50MB
            </Typography>
          </Box>

          {/* Request Type Dropdown */}
          <Stack spacing={1}>
            <FormLabel htmlFor="requestType">
              <Typography variant="h6">Request type</Typography>
            </FormLabel>
            <FormSelector
              name="requestType"
              placeholder="Request type"
              options={requestTypeOptions}
              disabled={submitting}
            />
          </Stack>

          {/* File Upload Area */}
          {!file && (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: "8px",
                p: 8,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              onClick={handleChooseFile}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
              />
              <Button
                variant="contained"
                color="secondary"
                component="span"
                startIcon={<UploadIcon fill="background.paper" />}
                sx={{ mb: 1 }}
              >
                Choose XML file to upload
              </Button>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                <DragDropIcon />
                <Typography variant="body1">
                  or drag and drop your document here
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Error Section */}
          {errors.length > 0 && (
            <Alert
              severity="error"
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                "& .MuiAlert-message": {
                  overflow: "visible",
                },
              }}
            >
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Following fields have errors:
              </Typography>
              <List dense disablePadding>
                {errors.map((error, index) => (
                  <ListItem key={error} disablePadding>
                    <ListItemText
                      primary={`${index + 1}. ${error}`}
                      slotProps={{ primary: { variant: "body2" } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {/* File Upload Status */}
          {file && (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: "8px",
                p: 2,
              }}
              onClick={handleChooseFile}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {isUploading && <FileIcon />}
                {uploadResult && !errors.length && <FileSuccessIcon />}
                {errors.length > 0 && <FileErrorIcon />}
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {file.name}
                </Typography>
                {uploadResult && !isUploading && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xml"
                      onChange={handleFileInputChange}
                      style={{ display: "none" }}
                    />
                    <Button disabled={submitting}>Reupload</Button>
                  </>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteFile()
                  }}
                  disabled={submitting}
                  aria-label="delete file"
                >
                  <DeleteIcon fontSize="small" fill="error.main" />
                </IconButton>
              </Stack>

              {isUploading && (
                <LinearProgress
                  value={uploadProgress}
                  variant="determinate"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}
        </Stack>
      </FormProvider>
    </Modal>
  )
}
